import { Box, CircularProgress, FormControlLabel, GridLegacy as Grid, Stack, Switch } from '@mui/material'
import { CheckIcon, PencilSimpleIcon, XIcon } from '@phosphor-icons/react'
import type { ContentChipProps } from '../../piazza/content'
import { ContentChip } from '../../piazza/content'
import { IconButton } from '../../piazza/buttons'
import { TinyButton } from '../../piazza/buttons'
import { Checkbox } from '../../piazza/selectors'
import { Typography } from '../../piazza/text'
import { Headline } from '../../piazza/text/Headline/Headline'
import { tokens } from '../../../style-dictionary/output'
import { Controller, type Control, useWatch } from 'react-hook-form'
import type { ExternalValuationMethod, ExternalValuationMethodsCopy, ExternalValuationMethodsSelectionValues } from './types'

type SelectableMethod = keyof ExternalValuationMethodsCopy['methodLabels']

export const STANDARD_EXTERNAL_VALUATION_METHODS: SelectableMethod[] = ['land_value', 'income', 'cost', 'comparison']

export const FULL_EXTERNAL_VALUATION_METHODS: SelectableMethod[] = [
    ...STANDARD_EXTERNAL_VALUATION_METHODS,
    'investment_method',
    'discounted_cashflow',
]

const METHOD_COLORS: Record<SelectableMethod, ContentChipProps['color']> = {
    land_value: 'royal',
    income: 'blue',
    cost: 'turquoise',
    comparison: 'green',
    investment_method: 'leaf',
    discounted_cashflow: 'lime',
}

type ExternalValuationMethodsSelectionContentProps<TFieldValues extends ExternalValuationMethodsSelectionValues> = {
    control: Control<TFieldValues>
    confirmedMethods: ExternalValuationMethod[]
    copy: ExternalValuationMethodsCopy
    isEditing?: boolean
    hasMortgageLending?: boolean
    methods?: SelectableMethod[]
    showSensitivityMatrix?: boolean
    isSavingAssessment?: boolean
    isSavingSelection?: boolean
    onEdit?: () => void | Promise<void>
    onAccept?: () => void
    onCancel?: () => void
    onMethodDeselected?: (method: SelectableMethod) => void
}

export function ExternalValuationMethodsSelectionContent<TFieldValues extends ExternalValuationMethodsSelectionValues>({
    control,
    confirmedMethods,
    copy,
    isEditing = true,
    hasMortgageLending = false,
    methods = STANDARD_EXTERNAL_VALUATION_METHODS,
    showSensitivityMatrix = true,
    isSavingAssessment = false,
    isSavingSelection = false,
    onEdit,
    onAccept,
    onCancel,
    onMethodDeselected,
}: ExternalValuationMethodsSelectionContentProps<TFieldValues>) {
    const selectionControl = control as unknown as Control<ExternalValuationMethodsSelectionValues>
    const selectedMethods = useWatch({ control: selectionControl, name: 'selectedValuationMethods' })
    const confirmedMethodOptions = methods.filter((method) => confirmedMethods.includes(method))

    return (
        <>
            <Grid item xs={12}>
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050}>
                    <Headline title={copy.methodsTitle} variant="headline4" />
                    {isSavingAssessment ? (
                        <Box width="32px" height="32px" display="grid" sx={{ placeItems: 'center' }}>
                            <CircularProgress size={tokens.SizingIconS} />
                        </Box>
                    ) : (
                        onEdit && (
                            <TinyButton
                                label={copy.edit}
                                leftIcon={<PencilSimpleIcon />}
                                onClick={() => void onEdit()}
                                disabled={isEditing}
                            />
                        )
                    )}
                </Stack>
            </Grid>

            {!isEditing ? (
                <Grid item xs={12}>
                    <Stack direction="row" gap={tokens.SizingSpacing100} flexWrap="wrap">
                        {confirmedMethodOptions.length ? (
                            confirmedMethodOptions.map((method) => (
                                <ContentChip key={method} size="small" color={METHOD_COLORS[method]} label={copy.methodLabels[method]} />
                            ))
                        ) : (
                            <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                {copy.noMethodsSelected}
                            </Typography>
                        )}
                    </Stack>
                </Grid>
            ) : (
                <>
                    <Grid item xs={12}>
                        <Stack direction="column" gap={tokens.SizingSpacing050}>
                            {methods.map((method) => (
                                <Stack
                                    key={method}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    minHeight="32px"
                                    padding={tokens.SizingSpacing075}
                                    borderRadius={tokens.SizingCornerM}
                                    bgcolor={
                                        selectedMethods[method] ? tokens.ColorSurfaceSelectedDefault : tokens.ColorSurfaceInverseDefault
                                    }
                                >
                                    <Controller
                                        control={selectionControl}
                                        name={`selectedValuationMethods.${method}`}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                sx={{ margin: 0, gap: tokens.SizingSpacing075 }}
                                                control={
                                                    <Switch
                                                        sx={methodSwitchStyles}
                                                        checked={Boolean(field.value)}
                                                        onChange={(event) => {
                                                            field.onChange(event.target.checked)
                                                            if (!event.target.checked) {
                                                                onMethodDeselected?.(method)
                                                            }
                                                        }}
                                                        disableRipple
                                                    />
                                                }
                                                label={
                                                    <Typography variant="textS" color={tokens.ColorTextBold}>
                                                        {copy.methodLabels[method]}
                                                    </Typography>
                                                }
                                                disableTypography
                                            />
                                        )}
                                    />
                                    {STANDARD_EXTERNAL_VALUATION_METHODS.includes(method) && hasMortgageLending && (
                                        <Controller
                                            control={selectionControl}
                                            name={`selectedMortgageLendingValuationMethods.${method}`}
                                            render={({ field }) => (
                                                <FormControlLabel
                                                    sx={{
                                                        margin: 0,
                                                        marginLeft: 'auto',
                                                        gap: tokens.SizingSpacing075,
                                                        '& svg': { width: tokens.SizingIconS, height: tokens.SizingIconS },
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            checked={Boolean(field.value)}
                                                            disabled={!selectedMethods[method]}
                                                            onChange={(_, checked) => field.onChange(checked)}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            variant="textS"
                                                            color={
                                                                selectedMethods[method] ? tokens.ColorTextBold : tokens.ColorTextDisabled
                                                            }
                                                        >
                                                            {copy.mortgageLending}
                                                        </Typography>
                                                    }
                                                    disableTypography
                                                />
                                            )}
                                        />
                                    )}
                                </Stack>
                            ))}
                            {showSensitivityMatrix && (
                                <Controller
                                    control={selectionControl}
                                    name="includesSensitivityMatrix"
                                    render={({ field }) => (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            minHeight="32px"
                                            padding={tokens.SizingSpacing075}
                                            borderRadius={tokens.SizingCornerM}
                                            bgcolor={field.value ? tokens.ColorSurfaceSelectedDefault : tokens.ColorSurfaceInverseDefault}
                                        >
                                            <FormControlLabel
                                                sx={{ margin: 0, gap: tokens.SizingSpacing075 }}
                                                control={
                                                    <Switch
                                                        sx={methodSwitchStyles}
                                                        checked={Boolean(field.value)}
                                                        onChange={(event) => field.onChange(event.target.checked)}
                                                        disableRipple
                                                    />
                                                }
                                                label={
                                                    <Typography variant="textS" color={tokens.ColorTextBold}>
                                                        {copy.sensitivityMatrix}
                                                    </Typography>
                                                }
                                                disableTypography
                                            />
                                        </Stack>
                                    )}
                                />
                            )}
                        </Stack>
                    </Grid>
                    {(onAccept || onCancel) && (
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="flex-end" gap={tokens.SizingSpacing100}>
                                {onAccept && (
                                    <IconButton
                                        size="small"
                                        variant="primary"
                                        icon={<CheckIcon />}
                                        onClick={onAccept}
                                        ariaLabel={copy.submit}
                                        disabled={isSavingSelection}
                                    />
                                )}
                                {onCancel && (
                                    <IconButton
                                        size="small"
                                        variant="secondary"
                                        icon={<XIcon />}
                                        onClick={onCancel}
                                        ariaLabel={copy.cancel}
                                        disabled={isSavingSelection}
                                    />
                                )}
                            </Stack>
                        </Grid>
                    )}
                </>
            )}
        </>
    )
}

const methodSwitchStyles = {
    width: '34px',
    height: '20px',
    padding: 0,
    overflow: 'visible',
    '& .MuiSwitch-switchBase': {
        padding: 0,
        color: tokens.ColorSurfaceDefault,
        '&.Mui-checked': {
            transform: 'translateX(14px)',
            color: tokens.ColorSurfaceDefault,
            '& + .MuiSwitch-track': {
                backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
                opacity: 1,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: '20px',
        height: '20px',
    },
    '& .MuiSwitch-track': {
        height: '12px',
        margin: '4px 0',
        borderRadius: tokens.SizingCornerRound,
        backgroundColor: tokens.ColorSurfaceInversePressed,
        opacity: 1,
    },
}
